import { useMemo } from 'react';
import type { Project, DashboardProject } from '../../project/types/project';
import { useProjectFilters } from '../../project/hooks/useProjectFilters';

interface UseHomePageStateProps {
  onProjectSelect: (project: DashboardProject) => void;
}

export const useHomePageState = ({ onProjectSelect }: UseHomePageStateProps) => {
  // Sample project data - In real app, this would come from an API
  const projects = useMemo<Project[]>(() => Array.from({ length: 12 }, (_, i) => ({
    id: `project-${i + 1}`,
    name: `Project ${i + 1}`,
    number: `PRJ-${(i + 1).toString().padStart(3, '0')}`,
    customer: `Customer ${(i % 3) + 1}`,
    startDate: '2024-01-01',
    endDate: '2024-12-31',
    type: (i % 3 === 0 ? 'Pipeline' : i % 3 === 1 ? 'Fabrication' : 'Others') as Project['type'],
    status: (i % 3 === 0 ? 'In Progress' : i % 3 === 1 ? 'Completed' : 'On Hold') as Project['status'],
    projectTypes: {
      pipeline: i===2 || i % 3 === 0,  //if i is 2 or 0, then it is pipeline
      fabrication: i===2 || i % 3 === 1, //if i is 2 or 1, then it is fabrication
      other: i % 3 === 2
    },
    fabricationOptions: {
      buckleArrestor:  i % 2 === 0,
      pipeInPipe:  i % 2 === 1,
      bulkhead: i===2 || i % 3 === 0
    },
    features: {
      welding: true,
      coating: i % 2 === 0,
      assembly: true,
      testing: i % 2 === 1,
      inspection: true
    },
    projectScope: {
      engineering: true,
      procurement: i % 2 === 0,
      construction: true,
      commissioning: i % 2 === 1
    },
    location: `Location ${(i % 4) + 1}`
  })), []);

  const {
    filteredProjects,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    resetFilters,
    uniqueCustomers,
    hasActiveFilters
  } = useProjectFilters({ projects });

  const handleProjectClick = (project: Project) => {
    const dashboardProject: DashboardProject = {
      id: project.id,
      projectName: project.name,
      projectNumber: project.number,
      description: `${project.name} - ${project.type} Project`,
      location: project.location,
      customer: project.customer,
      startDate: project.startDate,
      endDate: project.endDate,
      status: mapProjectStatus(project.status),
      projectTypes: {
        pipeline: project.projectTypes.pipeline,
        fabrication: project.projectTypes.fabrication,
        other: project.projectTypes.other
      },
      fabricationOptions: project.fabricationOptions,
      features: project.features,
      projectScope: project.projectScope,
      team: [], // Initialize with empty array if not available
      documents: [], // Initialize with empty array if not available
      materials: [] // Initialize with empty array if not available
    };
    
    onProjectSelect(dashboardProject);
  };

  const mapProjectStatus = (status: Project['status']): DashboardProject['status'] => {
    switch (status) {
      case 'In Progress':
        return 'active';
      case 'Completed':
        return 'completed';
      case 'On Hold':
        return 'on-hold';
      default:
        return 'active';
    }
  };

  return {
    filteredProjects,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    resetFilters,
    uniqueCustomers,
    handleProjectClick,
    hasActiveFilters
  };
}; 